import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import TopNav from "../components/TopNav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Pencil, Trash2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamAliasesPage() {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const [editing, setEditing] = useState(null); // null | { mode: 'new'|'edit', id?, our_name, betway_name }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/team-aliases`, { headers: auth });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Load failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => setEditing({ mode: "new", our_name: "", betway_name: "" });
  const openEdit = (row) => setEditing({ mode: "edit", id: row.id, our_name: row.our_name, betway_name: row.betway_name });

  const save = async () => {
    if (!editing) return;
    const { mode, id, our_name, betway_name } = editing;
    if (!our_name.trim() || !betway_name.trim()) {
      toast.error("Both names are required.");
      return;
    }
    setSaving(true);
    try {
      if (mode === "new") {
        await axios.post(`${API}/team-aliases`, { our_name, betway_name }, { headers: auth });
        toast.success("Alias saved.");
      } else {
        await axios.patch(`${API}/team-aliases/${id}`, { our_name, betway_name }, { headers: auth });
        toast.success("Alias updated.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete alias "${row.our_name}" → "${row.betway_name}"?`)) return;
    try {
      await axios.delete(`${API}/team-aliases/${row.id}`, { headers: auth });
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const visible = items.filter(r => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.our_name || "").toLowerCase().includes(q) ||
           (r.betway_name || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-24" data-testid="team-aliases-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div
              className="text-[11px] uppercase inline-flex items-center gap-2 mb-3"
              style={{
                color: "var(--v3-text-3)",
                letterSpacing: "0.14em",
                fontFamily: "var(--v3-font-mono)",
              }}
            >
              <span className="h-1 w-1 rounded-full" style={{ background: "var(--v3-indigo)" }} />
              Admin · Betway
            </div>
            <h1
              className="text-4xl md:text-5xl"
              style={{ fontWeight: 500, letterSpacing: "-0.045em", lineHeight: 1.05 }}
            >
              Team Aliases
            </h1>
            <p
              className="mt-4 max-w-xl text-[14px] leading-relaxed"
              style={{ color: "var(--v3-text-2)" }}
            >
              Confirmed pairs of our team name mapped to the Betway spelling. Shared across
              every account — the Betway bot picks them up whenever a team's label differs
              from our data provider.
            </p>
          </div>
          <Button
            onClick={openNew}
            data-testid="team-aliases-new-btn"
            className="h-10 rounded-md text-[13px]"
            style={{
              background: "var(--v3-indigo)",
              color: "var(--v3-text)",
              fontWeight: 500,
            }}
          >
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> New alias
          </Button>
        </div>

        <div className="mb-6">
          <Input
            placeholder="Filter by team name…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            data-testid="team-aliases-filter"
            className="h-10 max-w-sm rounded-md"
            style={{ background: "var(--v3-surface)", border: "1px solid var(--v3-line)" }}
          />
        </div>

        <div
          className="rounded-md overflow-x-auto"
          style={{ background: "var(--v3-surface)", border: "1px solid var(--v3-line)" }}
        >
          <table className="w-full text-sm">
            <thead className="text-slate-400 text-left bg-slate-900">
              <tr className="border-b border-slate-800">
                <th className="p-3">Our name</th>
                <th>Betway name</th>
                <th>Source</th>
                <th>Updated</th>
                <th className="text-right pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="p-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && !visible.length && (
                <tr><td colSpan={5} className="p-6 text-center text-slate-500">
                  {items.length ? "No aliases match your filter." : "No aliases saved yet. Confirming a Name-review bet creates them automatically."}
                </td></tr>
              )}
              {!loading && visible.map(row => (
                <tr key={row.id} className="border-b border-slate-800/60 hover:bg-slate-800/40" data-testid={`alias-row-${row.id}`}>
                  <td className="p-3 text-white">{row.our_name}</td>
                  <td className="text-sky-300">{row.betway_name}</td>
                  <td className="text-slate-500 text-xs">{row.source || "—"}</td>
                  <td className="text-slate-500 text-xs">{(row.updated_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="text-right pr-3 whitespace-nowrap">
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 w-7 p-0 text-slate-300 hover:bg-slate-800"
                      onClick={() => openEdit(row)}
                      data-testid={`alias-edit-${row.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-500/10"
                      onClick={() => del(row)}
                      data-testid={`alias-delete-${row.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !saving) setEditing(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md" data-testid="alias-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing?.mode === "new" ? "New team alias" : "Edit team alias"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Our name</div>
              <Input
                value={editing?.our_name || ""}
                onChange={e => setEditing(prev => ({ ...prev, our_name: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-slate-100"
                placeholder="e.g. Al Shabab (SA)"
                data-testid="alias-dialog-our-name"
              />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Betway name</div>
              <Input
                value={editing?.betway_name || ""}
                onChange={e => setEditing(prev => ({ ...prev, betway_name: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-slate-100"
                placeholder="e.g. Al-Shabab FC"
                data-testid="alias-dialog-betway-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              onClick={() => setEditing(null)}
              disabled={saving}
              data-testid="alias-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={save}
              disabled={saving}
              data-testid="alias-dialog-save"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
